<template>
    <v-dialog
        v-model="showModel"
        fullscreen
        hide-overlay
        persistent
        transition="dialog-bottom-transition"
        style="overflow-y: auto"
    >
        <v-card flat class="fill-height of-y" style="overflow-y: auto; overflow-x: hidden">
            <v-layout column fill-height style="overflow-y: auto; overflow-x: hidden">
                <!--Card Title-->
                <v-flex shrink>
                    <v-card-title class="py-2 pr-2 title-color">
                        <v-icon left color="white">trending_up</v-icon>
                        <span class="title font-weight-light white--text">Active Users</span>
                        <v-spacer></v-spacer>

                        <v-text-field
                            class="mx-1"
                            label="Window (min)"
                            type="number"
                            min="1"
                            max="1440"
                            v-model.number="minutes"
                            dense
                            hide-details
                            flat
                            dark
                            solo-inverted
                            style="max-width: 110px"
                            @change="onMinutesChange"
                            @keyup.enter="loadUsers"
                        ></v-text-field>

                        <v-text-field
                            class="mx-1"
                            label="Refresh (sec)"
                            type="number"
                            min="5"
                            max="3600"
                            v-model.number="refreshSeconds"
                            dense
                            hide-details
                            flat
                            dark
                            solo-inverted
                            style="max-width: 120px"
                            @change="restartTimer"
                        ></v-text-field>

                        <v-tooltip bottom>
                            <template #activator="{on}">
                                <v-btn
                                    flat
                                    icon
                                    color="white"
                                    class="pa-0 ma-0 mx-1"
                                    v-on="on"
                                    @click="autoRefresh = !autoRefresh"
                                >
                                    <v-icon>{{ autoRefresh ? 'sync' : 'sync_disabled' }}</v-icon>
                                </v-btn>
                            </template>
                            {{ autoRefresh ? `Auto-refresh ON (every ${refreshSeconds}s)` : 'Auto-refresh OFF' }}
                        </v-tooltip>

                        <v-text-field
                            class="mx-1"
                            label="Search"
                            v-model="textFilter"
                            dense
                            hide-details
                            flat
                            clearable
                            dark
                            solo-inverted
                        ></v-text-field>

                        <v-tooltip bottom>
                            <template #activator="{on}">
                                <v-btn flat icon color="white" class="pa-0 ma-0" v-on="on" @click="loadUsers">
                                    <div style="line-height: 1;" class="text-xs-center">
                                        {{ filteredUsers.length }}
                                    </div>
                                </v-btn>
                            </template>
                            {{ lastRefreshedLabel }}
                        </v-tooltip>

                        <v-btn flat icon color="white" class="pa-0 ma-0" @click="loadUsers">
                            <v-icon>refresh</v-icon>
                        </v-btn>
                        <v-btn flat icon color="white" class="pa-0 ma-0" @click="showModel = false">
                            <v-icon>close</v-icon>
                        </v-btn>
                    </v-card-title>
                </v-flex>

                <!--Loading-->
                <v-flex v-if="loadingUsers && users.length === 0" class="pa-2 text-xs-center">
                    <v-progress-circular :size="40" :width="5" color="primary" indeterminate/>
                </v-flex>

                <!--Loaded-->
                <template v-else>
                    <v-flex xs12 class="scrollable fill-height">
                        <v-data-table
                            :headers="userHeaders"
                            :items="filteredUsers"
                            item-key="userId"
                            :pagination.sync="pageData"
                            class="fill-height scroll-table-with-foot"
                        >
                            <template v-slot:items="props">
                                <td>
                                    <span @click="copyStuff(props.item.entityId)" class="underline-hover">{{ props.item.entityId }}</span>
                                </td>
                                <td>
                                    <span @click="copyStuff(props.item.userId)" class="underline-hover">{{ props.item.userId }}</span>
                                </td>
                                <td>
                                    <span @click="copyStuff(props.item.entity)" class="underline-hover">{{ props.item.entity }}</span>
                                </td>
                                <td>
                                    <span @click="copyStuff(props.item.username)" class="underline-hover">{{ props.item.username }}</span>
                                </td>
                                <td>
                                    <span @click="copyStuff(props.item.user)" style="white-space: nowrap" class="underline-hover">{{ props.item.user }}</span>
                                </td>
                                <td style="white-space: nowrap">{{ props.item.latest ? format(new Date(props.item.latest), 'YYYY-MM-DD HH:mm:ss') : '-' }}</td>
                            </template>
                        </v-data-table>
                    </v-flex>
                </template>
            </v-layout>
        </v-card>
    </v-dialog>
</template>

<script>
import {codeBaseMixin} from "../../codeBaseMixin";
import format from "date-fns/format";
import {arrSplice} from "@/codeFunctions";

export default {
    mixins: [codeBaseMixin],
    props: {
        show: Boolean,
        userInfo: Object
    },
    data() {
        return {
            loadingUsers: false,
            users: [],
            userHeaders: [
                {text: 'Entity ID', value: 'entityId'},
                {text: 'User ID', value: 'userId'},
                {text: 'Entity', value: 'entity'},
                {text: 'Username', value: 'username'},
                {text: 'User', value: 'user'},
                {text: 'Last Active', value: 'latest'},
            ],
            format,
            pageData: {
                descending: true,
                page: 1,
                rowsPerPage: 25,
                sortBy: 'latest',
                totalItems: null
            },
            textFilter: null,
            minutes: 10,
            refreshSeconds: 60,
            autoRefresh: true,
            lastRefreshed: null,
        }
    },
    watch: {
        show: {
            immediate: true,
            handler(shown) {
                if (shown) {
                    this.loadUsers();
                    this.restartTimer();
                } else {
                    this.stopTimer();
                }
            }
        },
        autoRefresh() {
            this.restartTimer();
        }
    },
    beforeDestroy() {
        this.stopTimer();
    },
    methods: {
        copyStuff(stuff) {
            if (stuff === null || stuff === undefined) return;
            try {
                navigator.clipboard.writeText(String(stuff));
                this.$snack.info('Copied to clipboard');
            } catch (e) {
                console.log(e);
            }
        },

        onMinutesChange() {
            // Window changed — fetch with the new range immediately.
            this.loadUsers();
        },

        async loadUsers() {
            const win = (Number.isFinite(this.minutes) && this.minutes > 0) ? Math.min(this.minutes, 1440) : 10;
            try {
                this.loadingUsers = true;
                const data = await this.$newReq('get', `activeUsers/${win}`);
                this.users = Array.isArray(data) ? data : [];
                this.lastRefreshed = new Date();
            } catch (e) {
                console.log(e);
                this.$snack.networkError && this.$snack.networkError();
            } finally {
                this.loadingUsers = false;
            }
        },

        stopTimer() {
            if (this._refreshTimer) {
                clearInterval(this._refreshTimer);
                this._refreshTimer = null;
            }
        },

        restartTimer() {
            this.stopTimer();
            if (!this.show || !this.autoRefresh) return;
            const secs = (Number.isFinite(this.refreshSeconds) && this.refreshSeconds >= 5) ? Math.min(this.refreshSeconds, 3600) : 60;
            this._refreshTimer = setInterval(() => this.loadUsers(), secs * 1000);
        },
    },
    computed: {
        filteredUsers() {
            const start = [...this.users];
            if (this.textFilter) {
                const needle = (this.textFilter || '').toLowerCase();
                arrSplice(start, u => !(JSON.stringify(u) || '').toLowerCase().includes(needle));
            }
            return start;
        },

        lastRefreshedLabel() {
            if (!this.lastRefreshed) return 'Click to refresh';
            return `Updated ${format(this.lastRefreshed, 'HH:mm:ss')} — click to refresh`;
        },

        showModel: {
            get() { return this.show; },
            set(val) { this.$emit('update:show', val); }
        }
    }
}
</script>

<style scoped>
.underline-hover:hover {
    cursor: pointer;
    text-decoration: underline;
    font-weight: bold;
    color: #000;
}
</style>
